import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchAllRuns } from '../../../api/runs';
import {
  mapApiRunToSummary,
  type RunSummary,
} from '../utils/resultsFormatting';

export const useRunsPage = ({
  projectId,
  page,
  pageSize,
  runType,
  includeImportedHistory,
}: {
  projectId?: string;
  page: number;
  pageSize: number;
  runType: 'chaos' | 'functional';
  includeImportedHistory: boolean;
}) => {
  const [totalRuns, setTotalRuns] = useState(0);
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sequenceRef = useRef(0);
  const scope = `${projectId || ''}|${page}|${pageSize}|${runType}|${includeImportedHistory}`;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  const loadRuns = useCallback(async () => {
    const sequence = ++sequenceRef.current;
    const requestedScope = scope;
    const isCurrent = () => sequenceRef.current === sequence && scopeRef.current === requestedScope;
    setLoading(true);
    setError(null);

    if (!projectId) {
      setRuns([]);
      setLoading(false);
      return;
    }

    try {
      const response = await fetchAllRuns(
        projectId,
        pageSize,
        (page - 1) * pageSize,
        runType,
        includeImportedHistory
      );

      if (!isCurrent()) return;
      setRuns(response.data.map((run) => mapApiRunToSummary(run)));
      setTotalRuns(response.total);
    } catch (loadError: any) {
      if (!isCurrent()) return;
      console.error('Failed to load runs:', loadError);
      setError(loadError.message || 'Failed to load runs');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [includeImportedHistory, page, pageSize, projectId, runType, scope]);

  useEffect(() => {
    setRuns([]);
    setTotalRuns(0);
    void loadRuns();
    return () => { sequenceRef.current += 1; };
  }, [loadRuns]);

  return {
    totalRuns,
    runs,
    loading,
    error,
    loadRuns,
  };
};
